import { useEffect, useState } from "react";
import { InputfieldTypes } from "../../../../../enums/InputFieldAttributesEnums";
import InputField from "../../../../../reusable-components/TNInputField";
import InvoiceManagementServices from "../../../../../services/invoiceManagement.service";
import {
  filterFieldConstants,
  filterFieldToIdMap,
  filterFormFields,
  filterIdToFieldMap,
  getFilterFormFields,
  filterEmptyValues,
  getPreviousFilter,
} from "./constants";
import styles from "./FilterForm.module.scss";
import {
  InvoiceFilterIds,
  InvoiceStatus,
  QuickbookStatus,
} from "../../../../../enums/InvoiceManagementEnums";
import {
  FetchInvoiceFiltersDataType,
  FetchInvoiceFiltersType,
} from "../../../../../Types/invoiceManagementTypes";
import {
  getFilteredOptions,
  toLabelValueArray,
  getEnumOptions,
  arrayToOptions,
} from "../../../../../utils/getDropdownOptions";
import { disableFutureDates } from "../../../../../utils/disabledDateUtil";
import { useFormContext } from "react-hook-form";
import {
  QueryObserverResult,
  RefetchOptions,
  UseMutateAsyncFunction,
} from "@tanstack/react-query";
import { DateFormat } from "../../../../../enums/dateTimeFormat";
import {
  hasAnyEmptyObject,
  isEmptyValue,
} from "../../../../../utils/emptyStringCheck";

interface FilterFormProps {
  values?: object;
  setValues?: () => void;
  clickableOrder?: [];
  addClickableOrder?: () => void;
}

const FilterForm = (props?: FilterFormProps) => {
  const { values, setValues, clickableOrder, addClickableOrder } = props;
  const { getValues, watch, setValue } = useFormContext();
  const {
    FetchCustomerFilter,
    FetchJobsFilter,
    FetchOrdersFilter,
    FetchInvoicesFilter,
    FetchSyncStatusFilter,
    FetchInvoiceStatusFilter,
  } = InvoiceManagementServices();
  const [currentFilter, setCurrentFilter] = useState<InvoiceFilterIds>(null);

  const payload = {
    companyId: values[filterFieldConstants.CUSTOMER_ID_FILTER_NAME],
    jobId: values[filterFieldConstants.JOB_ID_FILTER_NAME],
    orderId: values[filterFieldConstants.ORDER_ID_FILTER_NAME],
    invoiceId: values[filterFieldConstants.INVOICE_ID_FILTER_NAME],
    syncStatus: values[filterFieldConstants.QUICKBOOK_STATUS_FILTER_NAME],
    invoiceStatus: values[filterFieldConstants.STATUS_FILTER_NAME],
  };

  const { refetch: getCustomerFilter, data: customerFilterData } =
    FetchCustomerFilter(
      filterEmptyValues(
        { ...payload, companyId: undefined },
        currentFilter,
        clickableOrder
      )
    );

  const { refetch: getJobsFilter, data: jobsFilterData } = FetchJobsFilter(
    filterEmptyValues(
      { ...payload, jobId: undefined },
      currentFilter,
      clickableOrder
    )
  );

  const { refetch: getOrdersFilter, data: ordersFilterData } =
    FetchOrdersFilter(
      filterEmptyValues(
        { ...payload, orderId: undefined },
        currentFilter,
        clickableOrder
      )
    );

  const { refetch: getInvoicesFilter, data: invoicesFilterData } =
    FetchInvoicesFilter(
      filterEmptyValues(
        { ...payload, invoiceId: undefined },
        currentFilter,
        clickableOrder
      )
    );

  const { refetch: getSyncStatusFilter, data: syncStatusFilterData } =
    FetchSyncStatusFilter(
      filterEmptyValues(
        { ...payload, syncStatus: undefined },
        currentFilter,
        clickableOrder
      )
    );

  const { refetch: getInvoiceStatusFilter, data: invoiceStatusFilterData } =
    FetchInvoiceStatusFilter(
      filterEmptyValues(
        { ...payload, invoiceStatus: undefined },
        currentFilter,
        clickableOrder
      )
    );
  const customerOptions = toLabelValueArray(
    customerFilterData?.customers ?? []
  );
  const jobIdOptions = toLabelValueArray(jobsFilterData?.jobs ?? []);
  const orderOptions = toLabelValueArray(ordersFilterData?.orders ?? []);
  const invoiceOptions = arrayToOptions(invoicesFilterData?.invoices ?? []);
  const statusOptions = arrayToOptions(
    invoiceStatusFilterData?.invoiceStatus ?? []
  );
  const quickbookStatusOptions = arrayToOptions(
    syncStatusFilterData?.syncStatus ?? []
  );
  const [customerSearchValue, setCustomerSearchValue] = useState("");
  const [jobIdSearchValue, setJobIdSearchValue] = useState("");
  const [orderSearchValue, setOrderSearchValue] = useState("");
  const [invoiceSearchValue, setInvoiceSearchValue] = useState("");
  const [statusSearchValue, setStatusSearchValue] = useState("");
  const [quickBookStatusSearchValue, setQuickBookStatusSearchValue] =
    useState("");

  const filterFormFields = getFilterFormFields({
    customerSearchValue,
    jobIdSearchValue,
    orderSearchValue,
    invoiceSearchValue,
    statusSearchValue,
    quickBookStatusSearchValue,
    setCustomerSearchValue,
    setJobIdSearchValue,
    setOrderSearchValue,
    setInvoiceSearchValue,
    setStatusSearchValue,
    setQuickBookStatusSearchValue,
  });

  const getOptionsList = (fieldName: string) => {
    switch (fieldName) {
      case filterFieldConstants.CUSTOMER_ID_FILTER_NAME:
        return (
          getFilteredOptions(customerOptions ?? [], customerSearchValue) ?? []
        );
      case filterFieldConstants.JOB_ID_FILTER_NAME:
        return getFilteredOptions(jobIdOptions ?? [], jobIdSearchValue) ?? [];
      case filterFieldConstants.ORDER_ID_FILTER_NAME:
        return getFilteredOptions(orderOptions ?? [], orderSearchValue) ?? [];
      case filterFieldConstants.INVOICE_ID_FILTER_NAME:
        return (
          getFilteredOptions(invoiceOptions ?? [], invoiceSearchValue) ?? []
        );
      case filterFieldConstants.STATUS_FILTER_NAME:
        return getFilteredOptions(statusOptions ?? [], statusSearchValue) ?? [];
      case filterFieldConstants.QUICKBOOK_STATUS_FILTER_NAME:
        return (
          getFilteredOptions(
            quickbookStatusOptions ?? [],
            quickBookStatusSearchValue
          ) ?? []
        );
    }
  };

  const handleFieldOnBlur = (filterField: InvoiceFilterIds) => {
    let updatedClickOrder = clickableOrder?.filter(
      (filterId) => values[filterIdToFieldMap[filterId]]?.length > 0
    );
    if (
      updatedClickOrder?.length <
      clickableOrder?.filter((val) => val !== null)?.length
    ) {
      filterField = getPreviousFilter(filterField, clickableOrder);
      addClickableOrder(updatedClickOrder);
    } else if (!clickableOrder.includes(filterField)) {
      updatedClickOrder = [...updatedClickOrder, filterField];
      addClickableOrder(updatedClickOrder);
    }
    const filterFuncMap = {
      [InvoiceFilterIds.CUSTOMERS]: getCustomerFilter,
      [InvoiceFilterIds.JOBS]: getJobsFilter,
      [InvoiceFilterIds.ORDERS]: getOrdersFilter,
      [InvoiceFilterIds.INVOICES]: getInvoicesFilter,
      [InvoiceFilterIds.SYNC_STATUS]: getSyncStatusFilter,
      [InvoiceFilterIds.INVOICE_STATUS]: getInvoiceStatusFilter,
    };

    const allPossibleFunctions = Object?.values(filterFuncMap);
    if (
      (filterField === null || filterField === undefined) &&
      (updatedClickOrder[0] === null || updatedClickOrder[0] === undefined)
    ) {
      allPossibleFunctions?.forEach((func) => func());
      return;
    }
    if (
      updatedClickOrder?.length === 1 &&
      updatedClickOrder[0] === filterField &&
      filterField !== null
    ) {
      const func = filterFuncMap[filterField];
      func();
    }

    const getSubsequentFunctions = (selectedFilter, order) => {
      if (!selectedFilter || !order || order.length === 0) {
        return [];
      }
      const cleanSelectedFilter = String(selectedFilter).trim().toUpperCase();
      const currentIndex = order.findIndex(
        (item) => String(item).trim().toUpperCase() === cleanSelectedFilter
      );
      if (currentIndex === -1) {
        return [];
      }
      const subsequentFilterIds = order.slice(currentIndex + 1);
      const functionsToCall = subsequentFilterIds
        .map((id) => filterFuncMap[id])
        .filter((func) => func !== undefined);

      return functionsToCall;
    };

    if (!filterField || !updatedClickOrder || updatedClickOrder.length === 0) {
      return;
    }

    const cleanSelectedFilter = String(filterField).trim().toUpperCase();

    const currentIndex = updatedClickOrder.findIndex(
      (item) => String(item).trim().toUpperCase() === cleanSelectedFilter
    );

    if (currentIndex === -1) {
      return;
    }
    const filterIdsUpToCurrentIndex = updatedClickOrder?.slice(
      0,
      currentIndex + 1
    );
    const functionsUpToIndex = filterIdsUpToCurrentIndex
      ?.map((id) => filterFuncMap[id])
      ?.filter((func) => func !== undefined);
    const remainingFunctions = allPossibleFunctions?.filter(
      (func) => !functionsUpToIndex?.includes(func)
    );
    remainingFunctions?.forEach((func) => func());
  };

  useEffect(() => {
    if (Object?.keys?.(values)?.length === 0) {
      handleFieldOnBlur(null);
      setCurrentFilter(null);
      addClickableOrder([]);
      return;
    }
    handleFieldOnBlur(currentFilter);
  }, [values]);

  useEffect(() => {
    const fieldName = filterFieldConstants.CUSTOMER_ID_FILTER_NAME;
    const selected = values?.[fieldName] ?? [];
    if (customerOptions?.length > 0 && selected?.length > 0) {
      const validIds = new Set(customerOptions?.map((opt) => opt?.value));
      const newSelected = selected?.filter((id) => validIds?.has(id));
      if (newSelected?.length !== selected?.length) {
        setValues((prev) => ({ ...prev, [fieldName]: newSelected }));
        setValue(fieldName, newSelected);
      }
    }
  }, [customerOptions]);

  useEffect(() => {
    const fieldName = filterFieldConstants.JOB_ID_FILTER_NAME;
    const selected = values?.[fieldName] ?? [];
    if (jobIdOptions?.length > 0 && selected?.length > 0) {
      const validIds = new Set(jobIdOptions?.map((opt) => opt.value));
      const newSelected = selected?.filter((id) => validIds.has(id));
      if (newSelected?.length !== selected?.length) {
        setValues((prev) => ({ ...prev, [fieldName]: newSelected }));
        setValue(fieldName, newSelected);
      }
    }
  }, [jobIdOptions]);

  useEffect(() => {
    const fieldName = filterFieldConstants.ORDER_ID_FILTER_NAME;
    const selected = values?.[fieldName] ?? [];
    if (orderOptions?.length > 0 && selected?.length > 0) {
      const validIds = new Set(orderOptions?.map((opt) => opt.value));
      const newSelected = selected?.filter((id) => validIds.has(id));
      if (newSelected?.length !== selected?.length) {
        setValues((prev) => ({ ...prev, [fieldName]: newSelected }));
        setValue(fieldName, newSelected);
      }
    }
  }, [orderOptions]);

  useEffect(() => {
    const fieldName = filterFieldConstants.INVOICE_ID_FILTER_NAME;
    const selected = values?.[fieldName] ?? [];
    if (invoiceOptions?.length > 0 && selected?.length > 0) {
      const validIds = new Set(invoiceOptions?.map((opt) => opt.value));
      const newSelected = selected?.filter((id) => validIds.has(id));
      if (newSelected?.length !== selected?.length) {
        setValues((prev) => ({ ...prev, [fieldName]: newSelected }));
        setValue(fieldName, newSelected);
      }
    }
  }, [invoiceOptions]);

  useEffect(() => {
    const fieldName = filterFieldConstants.STATUS_FILTER_NAME;
    const selected = values?.[fieldName] ?? [];
    if (statusOptions?.length > 0 && selected?.length > 0) {
      const validIds = new Set(statusOptions?.map((opt) => opt.value));
      const newSelected = selected?.filter((id) => validIds.has(id));
      if (newSelected?.length !== selected?.length) {
        setValues((prev) => ({ ...prev, [fieldName]: newSelected }));
        setValue(fieldName, newSelected);
      }
    }
  }, [statusOptions]);

  useEffect(() => {
    const fieldName = filterFieldConstants.QUICKBOOK_STATUS_FILTER_NAME;
    const selected = values?.[fieldName] ?? [];
    if (quickbookStatusOptions?.length > 0 && selected?.length > 0) {
      const validIds = new Set(quickbookStatusOptions?.map((opt) => opt.value));
      const newSelected = selected?.filter((id) => validIds.has(id));
      if (newSelected?.length !== selected?.length) {
        setValues((prev) => ({ ...prev, [fieldName]: newSelected }));
        setValue(fieldName, newSelected);
      }
    }
  }, [quickbookStatusOptions]);
  return (
    <div className={styles.FilterFormContainer}>
      {filterFormFields.map((field) => {
        const value = values[field.name] ?? [];
        const handleChange = (newValue) => {
          setValues((prev) => ({
            ...prev,
            [field.name]: newValue,
          }));
          setValue(field.name, newValue);
        };
        return (
          <div className={styles.fieldContainer}>
            <InputField
              dropdownClassName={styles.customDropDown}
              type={field.type}
              name={field.name}
              label={field.label}
              bordered={field.bordered}
              placeholder={field.placeholder}
              options={getOptionsList(field.name)}
              values={value}
              onChange={handleChange}
              resetCheckbox={() => handleChange([])}
              setSearch={field.setSearch}
              searchValue={field.searchValue}
              onFocus={() => setCurrentFilter(filterFieldToIdMap[field.name])}
              //DatePicker Specific
              allowDateClear={true}
              disabledDates={disableFutureDates}
              onRangePickerChange={(dates) => {
                if (!dates || dates.length === 0) {
                  setValue(field.name, null);
                } else {
                  const start = dates[0].format(DateFormat.MM_DD_YYYY_SLASHED);
                  const end = dates[1].format(DateFormat.MM_DD_YYYY_SLASHED);
                  setValue(field.name, [start, end]);
                }
              }}
              {...(field.type === InputfieldTypes.RANGE_PICKER
                ? {
                    className: styles.RangePicker,
                  }
                : {
                    className: styles.dropDown,
                  })}
            />
          </div>
        );
      })}
    </div>
  );
};

export default FilterForm;
